import React from "react";
import ScreenerHeader from "./ScreenerHeader";
import PediatricianPicker from "./PediatricianPicker";
import ScreenerFooter from "./ScreenerFooter";
import { AgeBand, SelectedProvider } from "../../pages/Screener";

const AGE_BANDS: readonly AgeBand[] = ["4–5", "6–11", "12–17"];

const PILLS: readonly { readonly label: string; readonly theme: string }[] = [
  { label: "Attention", theme: "attention" },
  { label: "Emotional wellbeing", theme: "emotional" },
  { label: "Behavior", theme: "behavior" },
  { label: "Parenting stress", theme: "parenting" }
];

type StartScreenProps = {
  readonly ageBand: AgeBand | null;
  readonly provider: SelectedProvider | null;
  readonly onAgeBandChange: (ageBand: AgeBand) => void;
  readonly onProviderChange: (provider: SelectedProvider | null) => void;
  readonly onBegin: () => void;
};

export default function StartScreen({
  ageBand,
  provider,
  onAgeBandChange,
  onProviderChange,
  onBegin
}: StartScreenProps) {
  const canBegin = ageBand !== null && provider !== null;

  return (
    <section className="screener-step screener-step--start" aria-label="Start">
      <ScreenerHeader />

      <div className="screener-start__intro">
        <h1 className="screener-heading screener-heading--xl">Family behavioral health screener</h1>
        <p className="screener-subtitle">PSC-17 + caregiver wellbeing · ~3 minutes · Instant clinical scoring</p>
      </div>

      <ul className="screener-pills" aria-hidden="true">
        {PILLS.map(pill => (
          <li key={pill.label} className={`screener-pill screener-pill--${pill.theme}`}>
            <span className="screener-pill__dot" />
            {pill.label}
          </li>
        ))}
      </ul>

      <div className="screener-card screener-start__card">
        <fieldset className="screener-fieldset">
          <legend className="screener-field__label">Child's age</legend>
          <div className="screener-agebands" role="radiogroup" aria-label="Child's age">
            {AGE_BANDS.map(band => (
              <button
                key={band}
                type="button"
                role="radio"
                aria-checked={ageBand === band}
                className={`screener-ageband${ageBand === band ? " screener-ageband--selected" : ""}`}
                onClick={() => onAgeBandChange(band)}
              >
                {band}
              </button>
            ))}
          </div>
        </fieldset>

        <PediatricianPicker value={provider} onChange={onProviderChange} />
      </div>

      <button
        type="button"
        className="screener-button screener-button--attention"
        disabled={!canBegin}
        onClick={onBegin}
      >
        Begin screener →
      </button>

      <ScreenerFooter />
    </section>
  );
}
